import { applyCourseFilter, getCourseGroupLabel, type Course, type User } from "src/components/CrewCourseOverviewTable";
import {
  aggregatedStatusColours,
  headerCell,
  statusCell,
  worstStatusFirst,
  type ExcelRow,
  type ExcelSheet,
  type ExportedStatus,
} from "src/utils/excelExport";

type Translate = (key: string, options?: Record<string, unknown>) => string;

// The filters exactly as the table receives them
type CrewOverviewFilters = Omit<Parameters<typeof applyCourseFilter>[0], "courses" | "users">;

const isVisibleCourse = (course: Course | string): course is Course => Boolean(course) && typeof course !== "string";

// Mirrors CrewCourseOverviewTable: one row per user, each theme or aircraft group a merged header over its courses
export const buildCrewOverviewSheet = (
  courses_: Course[][],
  users_: User[],
  filters: CrewOverviewFilters,
  t: Translate,
): ExcelSheet => {
  const { courses, users } = applyCourseFilter({ ...filters, courses: courses_, users: users_ });
  // Hidden columns stay in the groups as "" placeholders; the users' statuses already skip them
  const groups = courses.map((group) => group.filter(isVisibleCourse)).filter((group) => group.length > 0);

  const headerRows: ExcelRow[] = [
    [
      headerCell(t("certificate_type")),
      ...groups.flatMap((group) => [
        headerCell(getCourseGroupLabel(group[0], t), { columnSpan: group.length, align: "center" }),
        ...group.slice(1).map(() => null),
      ]),
    ],
    [
      headerCell(t("certificates")),
      ...groups.flatMap((group) => group.map((course) => headerCell(course.fullname || course.name))),
    ],
  ];

  // A cell is empty where the screen shows nothing: no status, or hidden by the status or purchased filter
  const statusesOf = (user: User): ExportedStatus[] =>
    user.positions.slice(1).map((data) =>
      data && typeof data === "object"
        ? {
            cell: statusCell(data.status, t, data.days),
            // data.validUntil is formatted for display, so the sort takes the certificate's own date
            validUntil: data.courseCertificate?.actions.downloadableCertificate?.validUntil,
          }
        : { cell: null, validUntil: null },
    );

  // The users with the worst status come first. The user name has the colour of the user's worst exported status.
  const bodyRows: ExcelRow[] = worstStatusFirst(users, statusesOf).map((user): ExcelRow => {
    const statusCells = statusesOf(user).map(({ cell }) => cell);
    return [{ value: user.positions[0], ...aggregatedStatusColours(statusCells) }, ...statusCells];
  });

  return {
    headerRows,
    bodyRows,
    columnWidths: [30, ...groups.flatMap((group) => group.map(() => 14))],
  };
};
